import { useCallback, useEffect, useRef, useState } from "react";
import api, { streamApi } from "@/lib/api";

export const aiError = (error) => typeof error.response?.data?.detail === "string" ? error.response.data.detail : error.message?.startsWith("ai_") ? error.message : "ai_unavailable";

export function useAssistant(userId, lang) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const controller = useRef(null);
  const currentId = useRef(null);
  const working = useRef(false);
  const epoch = useRef(0);
  const refreshHistory = useCallback(async (offset = 0) => {
    if (!userId) return;
    const snapshot = epoch.current;
    try { const { data } = await api.get("/ai/sessions", { params: { offset } }); if (snapshot === epoch.current) setHistory((old) => offset ? [...old, ...data] : data); }
    catch (e) { if (snapshot === epoch.current) setError(aiError(e)); }
  }, [userId]);
  useEffect(() => {
    epoch.current++; controller.current?.abort(); currentId.current = null; working.current = false;
    setSessionId(null); setMessages([]); setHistory([]); setBusy(false); setError(""); refreshHistory();
    return () => { epoch.current++; controller.current?.abort(); };
  }, [userId, refreshHistory]);

  const ensureSession = async () => {
    if (currentId.current) return currentId.current;
    const { data } = await api.post("/ai/sessions");
    currentId.current = data.id; setSessionId(data.id); return data.id;
  };
  const openSession = async (id) => {
    if (working.current) return;
    setLoading(true); setError("");
    const snapshot = ++epoch.current;
    try {
      const { data } = await api.get(`/ai/sessions/${id}`);
      if (snapshot !== epoch.current) return;
      currentId.current = id; setSessionId(id); setMessages(data.messages);
    } catch (e) { if (snapshot === epoch.current) setError(aiError(e)); }
    finally { if (snapshot === epoch.current) setLoading(false); }
  };
  const newSession = () => {
    if (working.current) return;
    epoch.current++; currentId.current = null; setSessionId(null); setMessages([]); setError(""); setLoading(false);
  };
  const send = async (text, provider) => {
    if (working.current || !text.trim()) return false;
    working.current = true; setBusy(true); setError("");
    const snapshot = epoch.current;
    const replyId = crypto.randomUUID().replaceAll("-", "");
    controller.current = new AbortController();
    try {
      const id = await ensureSession();
      if (snapshot !== epoch.current) return false;
      setMessages((old) => [...old, { id: `draft-${replyId}`, role: "user", kind: "text", text, status: "complete" },
        { id: replyId, role: "assistant", kind: "text", provider, text: "", status: "pending" }]);
      await streamApi(`/ai/sessions/${id}/messages`, { text, provider, lang, request_id: replyId }, controller.current.signal, (event) => {
        if (snapshot !== epoch.current) return;
        if (event.type === "delta") setMessages((old) => old.map((message) => message.id === replyId ? { ...message, text: message.text + event.text } : message));
        if (event.type === "done") setMessages((old) => old.map((message) => message.id === replyId ? { ...message, status: "complete" } : message));
      });
      if (snapshot === epoch.current) {
        const { data } = await api.get(`/ai/sessions/${id}`);
        setMessages(data.messages);
      }
      return true;
    } catch (e) {
      if (snapshot === epoch.current) { setError(e.name === "AbortError" ? "ai_interrupted" : aiError(e)); setMessages((old) => old.map((message) => message.id === replyId ? { ...message, status: "error" } : message)); }
      return false;
    } finally { if (snapshot === epoch.current) { working.current = false; setBusy(false); refreshHistory(); } }
  };
  const image = async (prompt) => {
    if (working.current) return false;
    working.current = true; setBusy(true); setError("");
    const snapshot = epoch.current;
    try {
      const id = await ensureSession();
      const { data } = await api.post(`/ai/sessions/${id}/images`, { prompt, request_id: crypto.randomUUID().replaceAll("-", "") });
      if (snapshot === epoch.current) setMessages((old) => [...old, data]);
      return true;
    } catch (e) { if (snapshot === epoch.current) setError(aiError(e)); return false; }
    finally { if (snapshot === epoch.current) { working.current = false; setBusy(false); refreshHistory(); } }
  };
  const remove = async (id) => {
    try { await api.delete(`/ai/sessions/${id}`); if (id === currentId.current) newSession(); await refreshHistory(); }
    catch (e) { setError(aiError(e)); }
  };
  return { sessionId, messages, history, busy, loading, error, setError, send, image, openSession, newSession, remove,
    refreshHistory, cancel: () => controller.current?.abort() };
}